import { useEffect } from 'react';

/**
 * Page-aware context registry for RxAssistant quick-action chips.
 *
 * Each tab that wants to expose context to the assistant registers a
 * `getSnapshot()` function under a stable `contextKey`. When the user
 * clicks a chip, the quick-action handler reads the active snapshot
 * for the current context key and embeds it in the prompt.
 *
 * Module-level singleton is intentional — keeps the wiring trivial
 * (no provider, no context value drilling) and avoids re-renders when
 * the snapshot updates (we never put the snapshot itself in React
 * state; chip clicks pull it fresh at click time).
 */

export type ContextSnapshot = {
  /** Short label shown on the preview pill — e.g. "Nutrients · 30d". */
  title: string;
  /** One-line natural summary the chip text or prompt template can
   *  interpolate when useful. */
  summary: string;
  /** Structured payload embedded inside the hidden <context> block of
   *  the assistant prompt. Must be JSON-serializable. */
  data: unknown;
  /** Rough char-count of the serialized payload. Used by the chip UI
   *  to render a "1.2 KB" hint and to gate the send when it would
   *  exceed a safe budget. */
  sizeChars: number;
};

export type SnapshotGetter = () => ContextSnapshot | null;

const registry = new Map<string, SnapshotGetter>();

/**
 * Register a snapshot getter for `key` while the calling component is
 * mounted. Subsequent registrations under the same key OVERWRITE the
 * previous getter — typical pattern is one registration per tab, and
 * tabs are mounted one-at-a-time (route-driven), so collisions are
 * the exception, not the rule.
 */
export const useAssistantContext = (key: string, getSnapshot: SnapshotGetter): void => {
  useEffect(() => {
    registry.set(key, getSnapshot);
    return () => {
      // Only unregister if WE'RE still the registered getter — guards
      // against React strict-mode double-effects unregistering a
      // newer registration.
      if (registry.get(key) === getSnapshot) {
        registry.delete(key);
      }
    };
  }, [key, getSnapshot]);
};

/** Read the snapshot for `key`, or `null` if nothing's registered. */
export const getAssistantContext = (key: string): ContextSnapshot | null => {
  const get = registry.get(key);
  if (!get) return null;
  try {
    return get();
  } catch {
    return null;
  }
};

/** True if any tab has registered a snapshot under `key`. Used by
 *  the chip rack to decide whether to show chips. */
export const hasAssistantContext = (key: string): boolean => registry.has(key);
